<template>
  <div class="mb-3">
    <h1>Выбранные рецепты</h1>
    <div
      v-if="!selectedRecipeIDs || selectedRecipeIDs.length === 0"
      class="text-center p-4 mt-4 mb-4 border rounded"
    >
      <i class="bi bi-cart-x" style="font-size: 3rem; color: #6c757d"></i>
      <h4 class="mt-3">Ваша корзина для готовки пуста!</h4>
      <p class="lead text-muted">
        Находите рецепты в приложении и нажимайте на иконку
        <i class="bi bi-cart-plus"></i>, чтобы добавить их сюда.
      </p>
      <hr class="my-4" />
      <p>Когда вы выберете все желаемые блюда, на этой странице вы сможете:</p>
      <ul class="list-unstyled text-start mx-auto" style="max-width: 400px">
        <li>
          <i class="bi bi-check-circle-fill text-success me-2"></i>Увидеть
          полный список рецептов.
        </li>
        <li>
          <i class="bi bi-check-circle-fill text-success me-2"></i>Сформировать
          единый список покупок.
        </li>
        <li>
          <i class="bi bi-check-circle-fill text-success me-2"></i>Перейти в
          режим пошагового приготовления.
        </li>
      </ul>
    </div>

    <div v-else>
      <!-- Selected Recipes List -->
      <div v-if="recipes.length > 0" class="mt-4 mb-4">
        <ul class="list-unstyled">
          <li
            v-for="recipe in recipes"
            :key="recipe.id"
            class="d-flex justify-content-between align-items-center mt-2"
          >
            <div class="d-flex align-items-center">
              <a :href="`#/recipes/${recipe.id}`">
                <img
                  class="me-3"
                  :src="`${$globalState.apiUrl}/image.get/${encodeURIComponent(
                    recipe.name
                  )}`"
                />
                <span>{{ recipe.name }}</span></a
              >
            </div>
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="removeRecipe(recipe.id)"
              title="Удалить из списка"
            >
              <i class="bi bi-trash"></i>
            </button>
          </li>
        </ul>
      </div>

      <!-- Action Buttons -->
      <div class="text-center mt-4 mb-4">
        <button
          @click="generateShoppingList"
          class="btn btn-primary me-2"
          :disabled="isGeneratingList"
        >
          <span
            v-if="isGeneratingList"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          Сформировать список покупок
        </button>
      </div>

      <!-- Shopping List Display -->
      <div v-if="Object.keys(shoppingList).length > 0" class="card mb-4">
        <div class="card-header">
          <h5>Список покупок</h5>
        </div>
        <div v-for="(items, category) in sortedShoppingList" :key="category">
          <h6 class="card-header bg-light-subtle">{{ category }}</h6>
          <ul class="list-group list-group-flush">
            <li
              v-for="(item, index) in items"
              :key="`${category}-${index}`"
              class="list-group-item d-flex align-items-center user-select-none"
            >
              <input
                v-show="false"
                class="form-check-input me-3"
                type="checkbox"
                v-model="item.checked"
                @change="updateShoppingList(item.name)"
                :id="`item-${category}-${index}`"
              />
              <label
                :for="`item-${category}-${index}`"
                class="d-flex align-items-center"
                style="cursor: pointer; width: 100%"
              >
                <div class="me-2">
                  <img
                    class="food"
                    v-show="item.imageLoaded"
                    :src="
                      item.image
                        ? `${$globalState.apiUrl}/image.get/logo-${item.image}`
                        : `${$globalState.apiUrl}/image.get/logo-${item.name}`
                    "
                    @load="item.imageLoaded = true"
                    @error="
                      item.image = 'food';
                      item.imageLoaded = false;
                    "
                    @click="generateImage(item)"
                  />
                  <div v-if="item.checked" class="checkmark-overlay">
                    <i class="bi bi-check-circle-fill"></i>
                  </div>
                </div>
                <span
                  :class="{
                    'text-decoration-line-through text-muted': item.checked,
                  }"
                >
                  {{ item.name }} - {{ item.quantity }}
                </span>
              </label>
            </li>
          </ul>
        </div>
      </div>

      <!-- Add custom item to shopping list -->
      <div v-if="Object.keys(shoppingList).length > 0" class="input-group mb-3">
        <input
          type="text"
          class="form-control"
          placeholder="Добавить свой продукт..."
          v-model="customItemName"
          @keyup.enter="addCustomItem"
        />
        <button
          class="btn btn-outline-secondary"
          type="button"
          @click="addCustomItem"
          :disabled="!customItemName.trim()"
        >
          <i class="bi bi-plus-circle"></i>
        </button>
      </div>

      <!-- Loading Spinner -->
      <div
        v-if="isLoading && recipes.length === 0"
        class="text-center mt-4 mb-4"
      >
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Загрузка...</span>
        </div>
        <p class="mt-2">Загрузка выбранных рецептов...</p>
      </div>

      <!-- Error message -->
      <div v-if="error" class="alert alert-danger mt-3">
        {{ error }}
      </div>

      <!-- "Start Cooking" Button -->
      <div
        v-if="Object.keys(shoppingList).length > 0"
        class="text-center mt-4 mb-4"
      >
        <a href="#/cooking-mode" class="btn btn-success" role="button"
          >Начать готовить</a
        >
      </div>
    </div>
  </div>
</template>

<script>
// import RecipeView from "../RecipeView.vue";

export default {
  name: "PageCookingList",
  components: {
    // RecipeView,
  },
  data() {
    return {
      recipes: [],
      isLoading: false,
      error: null,
      shoppingList: {},
      isGeneratingList: false,
      customItemName: "", // For the new input field
      saveTimeout: null, // For debouncing save requests
    };
  },
  computed: {
    selectedRecipeIDs() {
      return this.$globalState.selectedRecipes;
    },
    sortedShoppingList() {
      const sortedCategories = Object.keys(this.shoppingList).sort();
      const sortedList = {};
      for (const category of sortedCategories) {
        sortedList[category] = this.shoppingList[category];
      }
      return sortedList;
    },
  },

  mounted() {
    // Attempt to load a previously saved shopping list when the component is mounted.
    this.loadShoppingList();
  },

  watch: {
    selectedRecipeIDs: {
      immediate: true,
      handler(newIDs, oldIDs) {
        if (newIDs && newIDs.length > 0) {
          this.fetchSelectedRecipes(newIDs);
        } else {
          this.recipes = [];
          this.isLoading = false;
          this.error = null;
          // If the list of selected IDs becomes empty (and it wasn't empty before),
          // clear the shopping list.
          if (oldIDs && oldIDs.length > 0 && newIDs.length === 0) {
            this.shoppingList = {};
          }
        }
      },
    },
  },

  methods: {
    /**
     * Removes a recipe from the cooking list (both in the local component state
     * and in the global state), and also removes it from the local 'recipes'
     * array for immediate UI feedback.
     *
     * @param {number} recipeId - The ID of the recipe to remove.
     */
    async removeRecipe(recipeId) {
      try {
        await this.sendCmd(`cookinglist.remove/${recipeId}`);

        // Remove from the global selection list for immediate UI update
        const globalIndex = this.$globalState.selectedRecipes.indexOf(recipeId);
        if (globalIndex > -1) {
          this.$globalState.selectedRecipes.splice(globalIndex, 1);
        }

        // Also remove from the local 'recipes' array for immediate UI feedback.
        const localIndex = this.recipes.findIndex((r) => r.id === recipeId);
        if (localIndex > -1) {
          this.recipes.splice(localIndex, 1);
        }
      } catch (error) {
        console.error(
          `Failed to remove recipe ${recipeId} from cooking list:`,
          error
        );
        // Optionally show an error message to the user
      }
    },

    async fetchSelectedRecipes(ids) {
      this.isLoading = true;
      this.error = null;
      try {
        const responseRaw = await this.sendCmd(
          "recipes.getByIds",
          JSON.stringify(ids)
        );
        const responseObject = JSON.parse(responseRaw);
        const unpackedJsonString = await this.unpackData(responseObject.data);
        this.recipes = JSON.parse(unpackedJsonString);
      } catch (err) {
        console.error("Error fetching selected recipes:", err);
        this.error = "Не удалось загрузить данные рецептов.";
        this.recipes = [];
      } finally {
        this.isLoading = false;
      }
    },

    async generateShoppingList() {
      if (this.selectedRecipeIDs.length === 0) return;
      this.isGeneratingList = true;
      this.shoppingList = {}; // Clear the list before generating a new one
      try {
        const responseRaw = await this.sendCmd(
          "recipes.shoppingList",
          JSON.stringify(this.selectedRecipeIDs)
        );
        const categorizedList = JSON.parse(responseRaw);
        const processedList = {};
        for (const category in categorizedList) {
          processedList[category] = categorizedList[category].map((item) => ({
            ...item,
            checked: false,
          }));
        }
        this.shoppingList = processedList;
      } catch (err) {
        console.error("Error generating shopping list:", err);
      } finally {
        this.isGeneratingList = false;
      }
    },

    addCustomItem() {
      const name = this.customItemName.trim();
      if (!name) {
        return;
      }

      const category = "Прочее";
      if (!this.shoppingList[category]) {
        this.shoppingList[category] = [];
      }

      this.shoppingList[category].unshift({
        name: name,
        quantity: "", // Custom items don't have a quantity from a recipe
        checked: false,
      });

      this.customItemName = ""; // Clear the input field
    },

    async loadShoppingList() {
      try {
        const responseRaw = await this.sendCmd("shoppinglist.get");
        const savedList = JSON.parse(responseRaw);
        if (
          savedList &&
          typeof savedList === "object" &&
          !Array.isArray(savedList) &&
          Object.keys(savedList).length > 0
        ) {
          this.shoppingList = savedList;
          console.log("Successfully loaded saved shopping list.");
        }
      } catch (error) {
        console.error("Error loading saved shopping list:", error);
        this.shoppingList = {}; // Reset on error
      }
    },

    async updateShoppingList(item) {
      console.log("Updating shopping list item:", item);
      try {
        await this.sendCmd("shoppinglist.update", item);
        console.log("Shopping list item updated successfully.");
      } catch (error) {
        console.error("Error updating shopping list item:", error);
      }
    },

    showFullRecipe(recipe) {
      localStorage.setItem(
        "lastViewedRecipe",
        JSON.stringify({
          from: "cooking-list",
          id: recipe.id,
        })
      );
      window.location.hash = `/recipes/${recipe.id}`;
    },

    async generateImage(item) {
      // Skip if not admin
      if (!this.$globalState.accessAdmin) return false;

      // Skip if item.image is empty
      if (!item.image) return false;

      // Skip if name contain %
      if (item.name.includes("%")) return;

      console.log("Generating image for:", item.name);

      // Send command to generate an image
      try {
        await this.sendCmd("image.generate.food/" + item.name);
        console.log("Image generated successfully for:", item.name);
      } catch (error) {
        console.error("Error generating image:", error);
      }
    },
  },
};
</script>

<style scoped>
img {
  width: 70px;
  border-radius: 8px;
}
img.food {
  width: 50px;
  border: 1px solid var(--bs-border-color-translucent);
}

a {
  text-decoration: none;
  color: inherit;
}

.checkmark-overlay {
  position: absolute;
  top: 8px;
  left: 15px;
  background-color: var(--bs-success);
}
</style>
